import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { PaperPlaneTilt, ChatCircleDots } from "@phosphor-icons/react";

const NAME_KEY = "scu_chat_name";

export default function Chat() {
  const [messages, setMessages] = useState([]);
  const [username, setUsername] = useState(() => localStorage.getItem(NAME_KEY) || "");
  const [draft, setDraft] = useState("");
  const [joined, setJoined] = useState(() => Boolean(localStorage.getItem(NAME_KEY)));
  const scrollRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/messages?limit=200");
      setMessages(data);
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Failed to fetch chat messages", err);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      if (!mounted) return;
      await fetchMessages();
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, [fetchMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const join = (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    localStorage.setItem(NAME_KEY, username.trim());
    setJoined(true);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    const text = draft;
    setDraft("");
    try {
      await api.post("/chat/messages", { username, message: text });
      fetchMessages();
    } catch (err) {
      if (process.env.NODE_ENV === "development") console.error("Failed to send chat message", err);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-16" data-testid="chat-page">
      <div className="section-eyebrow">Live</div>
      <h1 className="font-display text-5xl lg:text-7xl uppercase mt-3">The Court</h1>
      <p className="text-dim mt-3 max-w-xl">Talk to the community of the SCU. Keep it savage, keep it real.</p>

      {!joined ? (
        <form onSubmit={join} className="scu-card p-8 mt-10 max-w-md" data-testid="chat-join-form">
          <label className="text-xs uppercase tracking-widest text-dim">Your Ring Name</label>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="e.g. ThunderHand"
            className="w-full bg-black border border-white/15 px-4 py-3 mt-2 focus:border-[#FF3B30] outline-none"
            data-testid="chat-username-input"
          />
          <button className="btn-red w-full mt-4" type="submit" data-testid="chat-join-btn">Enter The Court</button>
        </form>
      ) : (
        <div className="scu-card mt-10 flex flex-col h-[60vh]" data-testid="chat-room">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="chat-messages">
            {messages.length === 0 && (
              <div className="text-dim text-sm flex items-center gap-2">
                <ChatCircleDots size={18} /> Be the first to drop a message.
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className="flex gap-3" data-testid={`chat-msg-${m.id}`}>
                <div className="font-display text-[#FF3B30] text-sm tracking-widest min-w-[120px]">{m.username}</div>
                <div className="text-white/90 break-words">{m.message}</div>
              </div>
            ))}
          </div>
          <form onSubmit={send} className="border-t border-white/10 p-4 flex gap-3" data-testid="chat-send-form">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={`Speak, ${username}...`}
              className="flex-1 bg-black border border-white/15 px-4 py-3 focus:border-[#FF3B30] outline-none"
              data-testid="chat-message-input"
            />
            <button className="btn-red inline-flex items-center gap-2" type="submit" data-testid="chat-send-btn">
              <PaperPlaneTilt size={16} weight="bold" /> Send
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
